// Copy original image bytes to the authorized Sharp callable, one bounded image
// at a time. Browser fetches preserve originals and avoid a server URL-fetch API.
// Partial successes are delivered immediately; retries can skip completed copies.
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { getPromotionSizedPostType } from "../constants/promotion_post_types";
import { PROMOTION_IMAGE_MAX_BYTES } from "./promotion_upload_service";

const readOriginal = async (url, signal) => {
  const parsed = new URL(url);
  if (!["https:", "http:"].includes(parsed.protocol)) throw new Error("Source must be a public image URL.");
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, 30000);
  try {
    if (signal?.aborted) throw new Error("Image sizing was canceled.");
    let response;
    try {
      response = await fetch(url, { signal: controller.signal, credentials: "omit" });
    } catch {
      throw new Error("Cannot read the original image. Upload a local copy and retry.");
    }
    if (!response.ok) throw new Error("Cannot read the original image. Upload a local copy and retry.");
    if (Number(response.headers.get("content-length")) > PROMOTION_IMAGE_MAX_BYTES) throw new Error("Original images must be no larger than 5 MB.");
    let blob;
    if (response.body?.getReader) {
      const reader = response.body.getReader();
      const chunks = [];
      let bytes = 0;
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          bytes += value.byteLength;
          if (bytes > PROMOTION_IMAGE_MAX_BYTES) {
            await reader.cancel();
            throw new Error("Original images must be no larger than 5 MB.");
          }
          chunks.push(value);
        }
      } finally { reader.releaseLock(); }
      blob = new Blob(chunks);
    } else blob = await response.blob();
    if (!blob.size || blob.size > PROMOTION_IMAGE_MAX_BYTES) throw new Error("Original images must contain data and be no larger than 5 MB.");
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Unable to read original image bytes."));
      reader.onload = () => resolve(String(reader.result).split(",")[1]);
      reader.readAsDataURL(blob);
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
};

export const generateSizedPromotionImages = async ({ eventId = null, platform, postType, images, onImage, onProgress, signal }) => {
  const format = getPromotionSizedPostType(platform, postType);
  if (!format?.width || !format?.height || !format.maxImages) throw new Error("Choose an image Post Type with dimensions first.");
  if (!Array.isArray(images) || !images.length || images.length > format.maxImages) throw new Error("Choose images within the selected Post Type's image limit.");
  const generate = httpsCallable(functions, "generateSizedPromotionImage", { timeout: 120000 });
  const copies = [];
  for (let index = 0; index < images.length; index += 1) {
    if (signal?.aborted) throw new Error("Image sizing was canceled.");
    onProgress?.(`Sizing image ${index + 1} of ${images.length}...`);
    try {
      const imageBase64 = await readOriginal(images[index].url, signal);
      if (signal?.aborted) throw new Error("Image sizing was canceled.");
      const image = images[index];
      const result = await generate({ eventId, platform, postType, imageBase64,
        ...(image.crop !== undefined ? { crop: image.crop } : {}),
      });
      if (signal?.aborted) throw new Error("Image sizing was canceled.");
      const data = result.data;
      if (!data?.url || data.width !== format.width || data.height !== format.height || data.postType !== postType) throw new Error("Image sizing returned an unexpected size. Retry generation.");
      // An older deployed callable must not silently return padding when the
      // editor requested a crop. Require the server's applied crop metadata.
      if (image.crop && ["x", "y", "width", "height", "zoom"].some((key) => image.crop[key] !== undefined &&
          (!Number.isFinite(data.crop?.[key]) || Math.abs(data.crop[key] - image.crop[key]) > 0.00001))) {
        throw new Error("The image crop was not applied. Update the image sizing function and retry.");
      }
      const copy = { ...data, sourceUrl: images[index].url };
      copies.push(copy);
      onImage?.(copy);
    } catch (error) {
      throw new Error(`Could not size image ${index + 1}: ${error.message || "Image sizing failed."} Completed copies are retained.`);
    }
  }
  return copies;
};
